<template>
  <v-dialog
    v-model="dialog"
    :fullscreen="$vuetify.breakpoint.mobile"
    scrollable
    max-width="850px"
    transition="dialog-transition"
  >
    <v-card height="90vh" class="d-flex flex-column">
      <v-card-title class="header">
        <span class="subtitle-1 font-weight-regular">
          Entrega Nº {{ delivery.number }}
        </span>

        <span class="ml-3 subtitle-1 font-weight-regular">
          Agendada para {{ $format.dateBr(delivery.scheduled_to) }}
        </span>

        <v-spacer />
        <v-btn icon @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="flex-grow-1 overflow-y-auto pt-4 ">
        <!-- Informações da Venda -->
        <v-row>
          <v-col cols="12">
            <v-card outlined class="section">
              <v-card-subtitle class="section-header pb-2">
                <v-icon small color="primary" class="mr-2">mdi-cart</v-icon>
                Informações da Venda
              </v-card-subtitle>
              <v-card-text class="pt-2">
                <v-row dense>
                  <v-col cols="6" md="3">
                    <div class="info-item">
                      <span class="label">
                        Venda Nº
                      </span>
                      <span class="value primary--text">
                        {{ delivery.sale.number }}
                      </span>
                    </div>
                  </v-col>
                  <v-col cols="6" md="3">
                    <div class="info-item">
                      <span class="label">
                        Cliente
                      </span>
                      <span class="value">
                        <span v-if="hasCustomer">
                          {{ delivery.sale.customer.code }} -
                        </span>
                        {{ customer_name }}
                      </span>
                    </div>
                  </v-col>
                  <v-col cols="6" md="3" v-if="delivery.sale.salesman_id">
                    <div class="info-item">
                      <span class="label">
                        Vendedor
                      </span>
                      <span class="value">
                        {{ delivery.sale.salesman.name }}
                      </span>
                    </div>
                  </v-col>
                  <v-col
                    cols="6"
                    md="3"
                    v-if="
                      delivery.sale.salesman &&
                        delivery.sale.salesman.whatsapp_contact
                    "
                  >
                    <div class="info-item">
                      <span class="label">
                        Contato Vendedor
                      </span>
                      <span class="value">
                        {{ delivery.sale.salesman.whatsapp_contact }}
                      </span>
                    </div>
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="info-item">
                      <span class="label">
                        Venda Realizada em
                      </span>
                      <span class="value">
                        {{ $format.dateBr(delivery.sale.created_at) }}
                      </span>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Endereço de Entrega -->
        <v-row v-if="delivery.main_address">
          <v-col cols="12">
            <v-card outlined class="section">
              <v-card-subtitle class="section-header pb-2">
                <v-icon small color="primary" class="mr-2">
                  mdi-map-marker
                </v-icon>
                Endereço de Entrega
              </v-card-subtitle>
              <v-card-text class="pt-2">
                <v-row dense>
                  <v-col cols="12" md="6">
                    <div class="info-item mb-2">
                      <span class="label">Logradouro</span>
                      <span class="value">
                        {{ delivery.main_address.address }},
                        {{ delivery.main_address.number }}
                      </span>
                    </div>
                    <div class="info-item">
                      <span class="label">CEP</span>
                      <span class="value">
                        {{ $format.cep(delivery.main_address.postal_code) }}
                      </span>
                    </div>
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="info-item mb-2">
                      <span class="label">Bairro / Cidade</span>
                      <span class="value">
                        {{ delivery.main_address.district }},
                        {{ delivery.main_address.city }} -
                        {{ delivery.main_address.state }}
                      </span>
                    </div>
                    <div
                      v-if="delivery.main_address.complement"
                      class="info-item"
                    >
                      <span class="label">Complemento</span>
                      <span class="value">
                        {{ delivery.main_address.complement }}
                      </span>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Motorista -->
        <v-row>
          <v-col cols="12">
            <v-card outlined class="section">
              <v-card-subtitle class="section-header">
                <v-icon small color="primary" class="mr-2">
                  mdi-account-tie
                </v-icon>
                Motorista
              </v-card-subtitle>
              <v-card-text class="pt-5">
                <v-row dense>
                  <v-col cols="12" md="6">
                    <employee-select
                      :disabled="!canUpdate || disableUpdateDeliveryman"
                      v-model="delivery.deliveryman"
                      role-filter="motorista"
                      label="Selecionar"
                    />
                  </v-col>
                  <v-col
                    cols="12"
                    md="6"
                    v-if="$acl.isLogistic() || $acl.isManager()"
                  >
                    <v-btn
                      color="primary"
                      outlined
                      small
                      :disabled="
                        !canUpdate || disableUpdateDeliveryman || isLoading
                      "
                      @click="updateDeliveryman()"
                      class="mt-2"
                    >
                      Atualizar
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Contatos -->
        <v-row v-if="hasCustomer">
          <v-col cols="12">
            <v-card outlined class="section">
              <v-card-subtitle class="section-header pb-2">
                <v-icon small color="primary" class="mr-2">mdi-phone</v-icon>
                Contatos
              </v-card-subtitle>
              <v-card-text class="pt-2">
                <AppShowPhones :phones="delivery.sale.customer.phones" />
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Observações -->
        <v-row v-if="delivery.comment">
          <v-col cols="12">
            <v-card outlined class="section">
              <v-card-subtitle class="section-header pb-2">
                <v-icon small color="primary" class="mr-2">
                  mdi-note-text
                </v-icon>
                Observações
              </v-card-subtitle>
              <v-card-text class="pt-2">
                <div class="body-2 font-weight-regular">
                  {{ delivery.comment }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Produtos da Entrega -->
        <v-row>
          <v-col cols="12">
            <v-card outlined class="section">
              <v-card-subtitle class="section-header">
                <v-icon small color="primary" class="mr-2">
                  mdi-package-variant
                </v-icon>
                Produtos Da Entrega
              </v-card-subtitle>
              <v-card-text class="pt-2">
                <v-data-table
                  :headers="headers"
                  :items="delivery.delivery_products"
                  :items-per-page="-1"
                  hide-default-footer
                  disable-sort
                  class="caption"
                >
                  <template v-slot:[`item.product.code`]="{ item }">
                    {{ item.product ? item.product.code : "" }}
                  </template>

                  <template v-slot:[`item.product.name`]="{ item }">
                    <div
                      class="product-description"
                      style="word-break: break-word; white-space: normal;"
                      :title="item.product ? item.product.name : ''"
                    >
                      {{ item.product ? item.product.name : "" }}
                    </div>
                  </template>

                  <template v-slot:[`item.quantity`]="{ item }">
                    {{ $format.decimal(item.quantity) }}
                  </template>
                  <template
                    v-slot:[`item.product_variant.weight_gross`]="{ item }"
                  >
                    <span
                      v-if="
                        item.product_variant.weight_gross &&
                          item.product_variant.weight_gross > 0
                      "
                    >
                      {{
                        $format.decimal(
                          weightCalc(
                            item.product_variant.weight_gross,
                            item.quantity
                          )
                        )
                      }}Kg
                    </span>
                    <span v-else class="grey--text text--lighten-1">
                      <v-icon small color="warning" class="mr-2">
                        mdi-alert
                      </v-icon>
                    </span>
                  </template>
                </v-data-table>

                <div
                  class="d-flex align-center justify-space-between px-4 pt-4"
                  style="border-top: 1px solid #e0e0e0;"
                >
                  <div v-if="hasZeroWeightProduct" class="d-flex align-center">
                    <v-icon small color="warning" class="mr-1">
                      mdi-alert
                    </v-icon>
                    <span class="caption warning--text">
                      Produtos sem peso cadastrado - Total estará incorreto!
                    </span>
                  </div>
                  <div class="d-flex align-center ml-auto">
                    <v-icon small color="primary" class="mr-1">
                      mdi-scale
                    </v-icon>
                    <span class="subtitle-2 font-weight-bold secondary--text">
                      Peso Total: {{ $format.decimal(totalWeight) }}Kg
                    </span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Entregas da Venda -->
        <v-row>
          <v-col cols="12">
            <v-card outlined class="section">
              <v-card-subtitle class="section-header pb-2">
                <v-icon small color="primary" class="mr-2">
                  mdi-truck-delivery
                </v-icon>
                Entregas desta Venda
              </v-card-subtitle>
              <v-card-text class="pt-2">
                <v-data-table
                  :headers="deliveryHeaders"
                  :items="saleDeliveries || []"
                  :hide-default-footer="true"
                  class=""
                  no-data-text="Nenhuma entrega encontrada"
                >
                  <template #item.number="{ item }">
                    <div class="d-flex align-center">
                      <span class="text-body-2">{{ item.number }}</span>
                      <v-chip
                        v-if="item.id === delivery.id"
                        x-small
                        color="primary"
                        class="ml-2"
                      >
                        Atual
                      </v-chip>
                    </div>
                  </template>

                  <template #item.status="{ item }">
                    <v-chip x-small :color="getStatusColor(item.status)" dark>
                      {{ getStatusText(item.status) }}
                    </v-chip>
                  </template>

                  <template #item.created_at="{ item }">
                    <span class="text-body-2">
                      {{ $format.dateBr(item.created_at) }}
                    </span>
                  </template>

                  <template #item.actions="{ item }">
                    <v-btn
                      v-if="item.id !== delivery.id"
                      small
                      color="primary"
                      text
                      @click="viewDelivery(item.id)"
                    >
                      <v-icon x-small left>mdi-eye</v-icon>
                      Ver
                    </v-btn>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Logs da Entrega -->
        <v-row>
          <v-col cols="12">
            <v-card outlined class="section">
              <v-card-subtitle class="section-header pb-2">
                <v-icon small color="primary" class="mr-2">mdi-history</v-icon>
                Logs da Entrega
              </v-card-subtitle>
              <v-card-text class="pt-2">
                <v-row dense>
                  <v-col cols="12" md="6" v-if="delivery.created_by">
                    <div class="info-item mb-2">
                      <span class="label">
                        Registrado
                      </span>
                      <span class="value">
                        {{ delivery.created_by.name }} em
                        {{ $format.dateTimeBr(delivery.created_at) }}
                      </span>
                    </div>
                  </v-col>
                  <v-col cols="12" md="6" v-if="delivery.processing_by">
                    <div class="info-item mb-2">
                      <span class="label">
                        Processo
                      </span>
                      <span class="value">
                        {{ delivery.processing_by.name }} em
                        {{ $format.dateTimeBr(delivery.processing_at) }}
                      </span>
                    </div>
                  </v-col>
                  <v-col cols="12" md="6" v-if="delivery.in_route_by">
                    <div class="info-item mb-2">
                      <span class="label">
                        Em Rota
                      </span>
                      <span class="value">
                        {{ delivery.in_route_by.name }} em
                        {{ $format.dateTimeBr(delivery.in_route_at) }}
                      </span>
                    </div>
                  </v-col>
                  <v-col cols="12" md="6" v-if="delivery.delivered_at">
                    <div class="info-item mb-2">
                      <span class="label">
                        Entregue
                      </span>
                      <span class="value">
                        {{ delivery.delivered_by.name }} em
                        {{ $format.dateTimeBr(delivery.delivered_at) }}
                      </span>
                    </div>
                  </v-col>
                  <v-col cols="12" md="6" v-if="delivery.canceled_at">
                    <div class="d-flex flex-column mb-2">
                      <span
                        class="caption font-weight-regular grey--text text--darken-1 text-uppercase"
                      >
                        Cancelado
                      </span>
                      <span class="body-2 font-weight-regular">
                        {{ delivery.canceled_by.name }} em
                        {{ $format.dateTimeBr(delivery.canceled_at) }}
                      </span>
                    </div>
                  </v-col>
                </v-row>

                <!-- Logs de Edição -->
                <div
                  v-if="activityLogs && activityLogs.length > 0"
                  class="mt-3"
                >
                  <v-divider class="my-2"></v-divider>
                  <div
                    class="caption font-weight-regular grey--text text--darken-1 text-uppercase mb-2"
                  >
                    Histórico de Edições
                  </div>

                  <v-row dense>
                    <v-col
                      cols="12"
                      md="12"
                      v-for="log in activityLogs"
                      :key="log.id"
                    >
                      <div class="d-flex flex-column mb-1">
                        <span
                          class="caption font-weight-regular grey--text text--darken-1 text-uppercase d-flex align-center"
                        >
                          <v-icon
                            x-small
                            class="mr-1"
                            :color="getLogInfo(log.description).color"
                          >
                            {{ getLogInfo(log.description).icon }}
                          </v-icon>
                          {{ getLogInfo(log.description).text }}
                        </span>
                        <span class="body-2 font-weight-regular">
                          {{ log.causer ? log.causer.name : "Sistema" }}
                        </span>
                        <div
                          v-if="log.properties && log.properties.attributes"
                          class="grey--text caption"
                        >
                          {{ log.properties.attributes }}
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions class="actions" style="border-top: 1px solid #e0e0e0">
        <app-cancel-btn
          v-if="delivery.status !== 'canceled' && $acl.can('deliveryCancel')"
          :disabled="isLoading"
          @click="cancelDelivery()"
        />

        <v-btn
          v-if="isProcessing && ($acl.isManager() || $acl.isLogistic())"
          color="primary"
          outlined
          :disabled="isLoading"
          @click="deliveryUpdate()"
        >
          <v-icon left> mdi-pencil </v-icon>
          Editar
        </v-btn>

        <v-btn
          v-if="isProcessing && ($acl.isManager() || $acl.isLogistic())"
          color="secondary"
          outlined
          :disabled="isLoading"
          @click="ReverseDelivery()"
        >
          <v-icon left> mdi-undo </v-icon>
          Reverter Pendente
        </v-btn>

        <v-btn
          v-if="isInRoute && ($acl.isManager() || $acl.isLogistic())"
          color="warning"
          outlined
          :disabled="isLoading"
          @click="ReverseDelivery()"
        >
          <v-icon left> mdi-undo </v-icon>
          Reverter Processando
        </v-btn>

        <v-spacer />

        <v-btn text @click="dialog = false" class="ml-3">
          <v-icon left>mdi-close</v-icon>
          Fechar
        </v-btn>

        <template v-if="$acl.can('deliveryCreateEdit')">
          <v-btn
            v-if="isPending && $acl.can('updateToProcessing')"
            color="primary"
            outlined
            :disabled="isLoading"
            @click="updateToProcessing()"
          >
            <v-icon left> mdi-play </v-icon>
            Iniciar Processo
          </v-btn>
          <v-btn
            v-if="isProcessing && ($acl.isManager() || $acl.isLogistic())"
            color="primary"
            outlined
            :disabled="isLoading"
            @click="updateToInRoute()"
            class="ml-2"
          >
            <v-icon left>mdi-truck-fast</v-icon>
            Colocar em rota
          </v-btn>
          <v-btn
            v-if="isInRoute && ($acl.isManager() || $acl.isLogistic())"
            color="primary"
            :disabled="isLoading"
            @click="updateToDelivered()"
            class="ml-2"
          >
            <v-icon left>mdi-check-circle</v-icon>
            Marcar Entregue
          </v-btn>
        </template>
      </v-card-actions>
    </v-card>

    <!-- Component de Edição de Entrega -->
    <EditDelivery ref="EditDelivery" @update="handleDeliveryUpdate" />
  </v-dialog>
</template>

<script>
import AppShowPhones from "../../app/sections/AppShowPhones.vue";
import EmployeeSelect from "../../employees/ui/EmployeeSelect.vue";
import EditDelivery from "./EditDelivery.vue";
export default {
  components: { AppShowPhones, EmployeeSelect, EditDelivery },
  data() {
    return {
      dialog: false,
      isLoading: false,

      headers: [
        {
          text: "Código",
          value: "product.code",
          align: "center",
          sortable: false,
        },
        {
          text: "Descrição",
          value: "product.name",
          width: "100%",
          sortable: false,
        },
        {
          text: "Peso",
          value: "product_variant.weight_gross",
          width: "100%",
          align: "right",
          sortable: false,
        },
        {
          text: "Entregue",
          value: "quantity",
          align: "right",
          sortable: false,
        },
      ],

      deliveryHeaders: [
        {
          text: "Número",
          value: "number",
          sortable: false,
          width: "25%",
        },
        {
          text: "Status",
          value: "status",
          align: "center",
          sortable: false,
          width: "25%",
        },
        {
          text: "Criação",
          value: "created_at",
          align: "center",
          sortable: false,
          width: "25%",
        },
        {
          text: "Ações",
          value: "actions",
          align: "center",
          sortable: false,
          width: "25%",
        },
      ],

      deliveryId: null,
      delivery: {
        sale: {
          customer: {},
          salesman: {},
        },
        delivery_products: [],
        main_address: {},
      },
      activityLogs: [],
      saleDeliveries: [],
    };
  },

  created() {
    // this.open(5);
  },

  computed: {
    hasCustomer() {
      return this.delivery.sale.customer_id != null;
    },

    totalWeight() {
      return this.delivery.delivery_products.reduce((total, item) => {
        return (
          total +
          this.weightCalc(item.product_variant.weight_gross, item.quantity)
        );
      }, 0);
    },

    hasZeroWeightProduct() {
      return this.delivery.delivery_products.some(
        (item) => item.product_variant.weight_gross === 0
      );
    },

    customer_name() {
      return this.hasCustomer
        ? this.delivery.sale.customer.name
        : "Consumidor final";
    },

    user() {
      return this.$store.state.auth.userData.user;
    },

    isPending() {
      return this.delivery.status == "pending";
    },
    isProcessing() {
      return this.delivery.status == "processing";
    },
    isInRoute() {
      return this.delivery.status == "in_route";
    },

    canUpdate() {
      if (this.$acl.isLogistic() || this.$acl.isManager()) return true;

      return (
        this.delivery.status === "processing" &&
        this.delivery.employee_id == null
      );
    },

    disableUpdateDeliveryman() {
      return (
        this.delivery.status === "in_route" ||
        this.delivery.status === "delivered" ||
        this.delivery.status === "canceled"
      );
    },
  },

  methods: {
    getLogInfo(description) {
      const logTypes = {
        delivery_edited: {
          color: "primary",
          icon: "mdi-pencil",
          text: "Editado",
        },
        delivery_reverted_to_pending: {
          color: "secondary",
          icon: "mdi-undo",
          text: "Revertido Para Pendente",
        },
        default: {
          color: "warning",
          icon: "mdi-undo",
          text: "Revertido Para Processamento",
        },
      };

      return logTypes[description] || logTypes.default;
    },

    weightCalc(weight, quantity) {
      return weight * quantity;
    },

    async open(delivery_id) {
      this.deliveryId = delivery_id;

      await this.loadDelivery();

      this.dialog = true;
    },

    async deliveryUpdate() {
      this.$refs.EditDelivery.open(this.delivery.id);
    },

    async ReverseDelivery() {
      this.isLoading = true;
      this.$loading.start();

      await this.$http
        .update("sale/delivery-revert", this.delivery.id)
        .then((response) => {
          this.$emit("update");
          this.loadDelivery();
          this.$loading.finish();
        })
        .catch((error) => {
          this.$loading.finish();
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    async loadDelivery() {
      this.isLoading = true;
      this.$loading.start();

      await this.$http
        .show("sale/delivery", this.deliveryId)
        .then((response) => {
          this.delivery = response.delivery;
          this.activityLogs = response.activityLogs || [];
          this.saleDeliveries = response.saleDeliveries || [];
          this.$loading.finish();
        })
        .catch((error) => {
          this.$loading.finish();
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    async updateToProcessing() {
      this.isLoading = true;
      this.$loading.start();
      try {
        await this.updateStatus("processing");
        this.printCustomPreview();
      } catch (e) {
        console.error(e);
      } finally {
        this.$loading.finish();
        this.isLoading = false;
      }
    },

    printCustomPreview() {
      this.$print.customPreview(
        `/entregas/entrega/imprimir/${this.delivery.id}`
      );
    },

    async updateToInRoute() {
      this.isLoading = true;
      this.$loading.start();

      await this.updateStatus("in_route");

      this.$loading.finish();
      this.isLoading = false;
    },

    async updateToDelivered() {
      this.isLoading = true;
      this.$loading.start();

      await this.updateStatus("delivered");

      this.$loading.finish();
      this.isLoading = false;
    },

    async updateStatus(status) {
      try {
        const response = await this.$http.update(
          "sale/delivery",
          this.delivery.id,
          {
            status: status,
            employee_id: this.delivery.deliveryman
              ? this.delivery.deliveryman.id
              : null,
          }
        );

        this.$emit("update");
        this.loadDelivery();

        return response;
      } catch (error) {
        this.$loading.finish();
        this.isLoading = false;
        throw error;
      }
    },

    async updateDeliveryman() {
      this.isLoading = true;

      await this.$http
        .update("sale/delivery-update-deliveryman", this.delivery.id, {
          employee_id: this.delivery.deliveryman.id,
        })
        .then((response) => {
          this.$emit("update");
          this.loadDelivery();
        })
        .catch((error) => {
          this.$loading.finish();
          this.isLoading = false;
        });
    },

    async viewDelivery(deliveryId) {
      await this.open(deliveryId);
    },

    cancelDelivery() {
      this.isLoading = true;
      this.$loading.start();

      this.$http
        .destroy("sale/delivery", this.delivery.id)
        .then((response) => {
          this.loadDelivery();
          this.$emit("update");

          this.$loading.finish();
        })
        .catch((error) => {
          this.$loading.finish();
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    handleDeliveryUpdate() {
      this.$emit("update");
      this.loadDelivery();
    },

    // Status Helpers
    getStatusColor(status) {
      const statusColors = {
        processing: "orange",
        delivered: "success",
        canceled: "error",
        in_route: "primary",
        pending: "secondary",
      };
      return statusColors[status] || "grey";
    },

    getStatusText(status) {
      const statusTexts = {
        processing: "Processando",
        delivered: "Entregue",
        canceled: "Cancelada",
        cancelled: "Cancelada",
        in_route: "Em rota",
        pending: "Pendente",
      };
      return statusTexts[status] || status;
    },
  },
};
</script>

<style scoped>
.section-header {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background-color: #f5f5f5;
  margin: 0;
  padding: 8px 12px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
}

/* Informações compactas */
.info-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.label {
  font-size: 0.7rem;
  font-weight: 500;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.product-description {
  font-size: 0.8rem;
}

.value {
  font-size: 0.85rem;
  font-weight: 500;
  color: #333;
}
.actions {
  flex-shrink: 0;
  padding: 12px 16px;
  background: white;
  border-top: 1px solid #e0e0e0;
}

.header {
  flex-shrink: 0;
  padding: 12px 16px;
  background: white;
  border-bottom: 1px solid #e0e0e0;
}

/* Descrição do produto com truncamento */
.product-description {
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.8rem;
}
</style>
